import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  MessageSquare,
  Mic,
  Volume2,
  Database,
  HardDrive,
  FileText,
  FolderOpen,
  FolderTree,
  FileCode,
  Lightbulb,
  AlertCircle,
  CheckCircle2,
  Tag,
  Copy,
  Check,
} from 'lucide-react';
import { NeMoDataset, NeMoTaskType, NeMoDatasetFormat } from '@/mock/gpu-instance';
import { useState } from 'react';

interface NeMoDatasetCardProps {
  dataset: NeMoDataset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectDataset?: (dataset: NeMoDataset) => void;
}

const getTaskIcon = (task: NeMoTaskType) => {
  switch (task) {
    case 'llm':
      return <MessageSquare className="h-5 w-5 text-green-500" />;
    case 'asr':
      return <Mic className="h-5 w-5 text-blue-500" />;
    case 'tts':
      return <Volume2 className="h-5 w-5 text-purple-500" />;
  }
};

const getTaskBadge = (task: NeMoTaskType) => {
  const config: Record<NeMoTaskType, { bg: string; text: string; label: string }> = {
    llm: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'LLM Fine-tuning' },
    asr: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'Speech Recognition' },
    tts: { bg: 'bg-purple-500/10', text: 'text-purple-600', label: 'Text-to-Speech' },
  };
  const { bg, text, label } = config[task];
  return (
    <Badge className={`${bg} ${text} border-current/30`}>
      {getTaskIcon(task)}
      <span className="ml-1">{label}</span>
    </Badge>
  );
};

const getFormatIcon = (format: NeMoDatasetFormat) => {
  switch (format) {
    case 'jsonl':
      return <FileText className="h-4 w-4 text-green-500" />;
    case 'json_manifest':
      return <FolderOpen className="h-4 w-4 text-blue-500" />;
  }
};

// Get annotation example based on task and format
const getAnnotationExample = (task: NeMoTaskType, format: NeMoDatasetFormat): string => {
  if (task === 'llm') {
    return `// JSONL format - each line is a separate JSON object
// Note: In actual files, each entry is on a single line

// Basic instruction-response pair
{
  "messages": [
    {"role": "user", "content": "What is machine learning?"},
    {"role": "assistant", "content": "Machine learning is a subset of AI..."}
  ]
}

// With system prompt
{
  "messages": [
    {"role": "system", "content": "You are a helpful coding assistant."},
    {"role": "user", "content": "Write a Python function to sort a list"},
    {"role": "assistant", "content": "def sort_list(lst):\\n    return sorted(lst)"}
  ]
}

// Multi-turn conversation
{
  "messages": [
    {"role": "user", "content": "Hello"},
    {"role": "assistant", "content": "Hi! How can I help you today?"},
    {"role": "user", "content": "What's the weather like?"},
    {"role": "assistant", "content": "I don't have real-time weather data."}
  ]
}`;
  } else if (task === 'asr') {
    return `// JSON manifest format - each line describes one audio file
// Basic ASR annotation
{"audio_filepath": "/data/audio/sample_001.wav", "text": "hello world this is a sample transcription", "duration": 3.5}

// With additional metadata
{"audio_filepath": "/data/audio/sample_002.wav", "text": "the quick brown fox jumps over the lazy dog", "duration": 4.2, "sample_rate": 16000}

// Multilingual example
{"audio_filepath": "/data/audio/sample_003.wav", "text": "guten morgen wie geht es ihnen", "duration": 2.8, "lang": "de"}

// With speaker info (optional)
{"audio_filepath": "/data/audio/sample_004.wav", "text": "welcome to the conference", "duration": 1.9, "speaker_id": "speaker_001"}`;
  } else {
    // TTS
    return `// JSON manifest format for TTS - text to be synthesized with audio reference
// Single speaker TTS
{"audio_filepath": "/data/audio/ljspeech_001.wav", "text": "Printing, in the only sense with which we are at present concerned.", "duration": 4.5}

// Multi-speaker TTS with speaker ID
{"audio_filepath": "/data/audio/vctk_p225_001.wav", "text": "Please call Stella.", "duration": 1.2, "speaker": 0}
{"audio_filepath": "/data/audio/vctk_p226_001.wav", "text": "Please call Stella.", "duration": 1.3, "speaker": 1}

// With phoneme alignment (optional, for advanced training)
{"audio_filepath": "/data/audio/sample.wav", "text": "Hello world", "duration": 1.5, "speaker": 0, "normalized_text": "hello world"}

// With pitch/energy info (optional)
{"audio_filepath": "/data/audio/expressive.wav", "text": "That's amazing!", "duration": 1.1, "speaker": 0, "emotion": "excited"}`;
  }
};

// Get labeling tips based on task
const getLabelingTips = (task: NeMoTaskType): string[] => {
  switch (task) {
    case 'llm':
      return [
        'Use clear, unambiguous instructions in user messages',
        'Assistant responses should be complete and well-formatted',
        'Include system prompts for specialized behaviors',
        'Maintain consistent formatting across all examples',
        'Use diverse examples covering different aspects of the task',
        'Balance the dataset with different response lengths',
        'Escape special characters properly in JSON strings',
      ];
    case 'asr':
      return [
        'Transcribe exactly what is spoken (verbatim)',
        'Use lowercase unless proper nouns are involved',
        'Include punctuation for natural reading',
        'Mark hesitations and filler words if relevant',
        'Ensure audio duration matches the actual file',
        'Use consistent audio format (WAV 16kHz recommended)',
        'Normalize audio volume across samples',
      ];
    case 'tts':
      return [
        'Use clean, studio-quality audio recordings',
        'Normalize text (expand abbreviations, numbers)',
        'Maintain consistent speaking style per speaker',
        'Remove background noise and artifacts',
        'Ensure speaker IDs are consistent across files',
        'Use 22050 Hz or higher sample rate',
        'Trim silence at the beginning and end',
      ];
  }
};

// Get common mistakes based on task
const getCommonMistakes = (task: NeMoTaskType): string[] => {
  switch (task) {
    case 'llm':
      return [
        'Malformed JSON (missing quotes, trailing commas)',
        'Inconsistent role names (use "user"/"assistant"/"system")',
        'Empty or placeholder responses',
        'Unescaped newlines in JSON strings',
        'Mixing languages without proper handling',
        'Overly long sequences exceeding model limits',
      ];
    case 'asr':
      return [
        'Incorrect duration values (must match actual audio)',
        'File paths not matching actual file locations',
        'Audio format mismatches (sample rate, channels)',
        'Missing or corrupted audio files',
        'Transcription errors or typos',
        'Inconsistent text normalization',
      ];
    case 'tts':
      return [
        'Audio quality issues (clipping, noise)',
        'Inconsistent speaker IDs',
        'Text not matching spoken content',
        'Incorrect duration values',
        'Missing speaker embeddings for multi-speaker',
        'Unnormalized text (numbers, abbreviations)',
      ];
  }
};

// Get directory structure based on task
const getDirectoryStructure = (task: NeMoTaskType): string => {
  if (task === 'llm') {
    return `dataset/
├── train.jsonl          # Training data (one JSON per line)
├── val.jsonl            # Validation data
├── test.jsonl           # Test data (optional)
└── README.md            # Dataset documentation (optional)

# Each .jsonl file contains:
# {"messages": [{"role": "user", "content": "..."}, {"role": "assistant", "content": "..."}]}
# {"messages": [{"role": "system", "content": "..."}, {"role": "user", "content": "..."}, ...]}
# ...`;
  } else if (task === 'asr') {
    return `dataset/
├── audio/
│   ├── train/
│   │   ├── sample_001.wav
│   │   ├── sample_002.wav
│   │   └── ...
│   └── val/
│       └── ...
├── manifests/
│   ├── train_manifest.json    # One JSON per line
│   └── val_manifest.json
└── README.md

# Supported audio formats: WAV (recommended), FLAC, MP3
# Recommended: 16kHz mono WAV`;
  } else {
    return `dataset/
├── audio/
│   ├── speaker_0/
│   │   ├── sample_001.wav
│   │   ├── sample_002.wav
│   │   └── ...
│   └── speaker_1/
│       └── ...
├── manifests/
│   ├── train_manifest.json
│   └── val_manifest.json
└── speaker_embeddings/        # Optional for multi-speaker
    ├── speaker_0.npy
    └── speaker_1.npy

# Recommended: 22050Hz or 44100Hz mono WAV`;
  }
};

const NeMoDatasetCard = ({
  dataset,
  open,
  onOpenChange,
  onSelectDataset,
}: NeMoDatasetCardProps) => {
  const [copiedExample, setCopiedExample] = useState(false);

  if (!dataset) return null;

  const handleCopyExample = async () => {
    try {
      await navigator.clipboard.writeText(dataset.formatExample);
      setCopiedExample(true);
      setTimeout(() => setCopiedExample(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSelect = () => {
    onSelectDataset?.(dataset);
    onOpenChange(false);
  };

  const labelingTips = getLabelingTips(dataset.task);
  const commonMistakes = getCommonMistakes(dataset.task);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/10 to-blue-500/10">
                <Database className="h-8 w-8 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">{dataset.displayName}</DialogTitle>
                <DialogDescription className="mt-1">
                  {dataset.format === 'jsonl' ? 'JSONL' : 'JSON Manifest'} format dataset for {dataset.task.toUpperCase()} training
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Task & Format Badges */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {getTaskBadge(dataset.task)}
          <Badge variant="outline" className="font-mono">
            {getFormatIcon(dataset.format)}
            <span className="ml-1">{dataset.format === 'jsonl' ? 'JSONL' : 'JSON Manifest'}</span>
          </Badge>
          <Badge variant="secondary">
            {dataset.totalSize}
          </Badge>
          {dataset.language && (
            <Badge variant="secondary">
              {dataset.language.toUpperCase()}
            </Badge>
          )}
        </div>

        <Separator className="my-4" />

        {/* Dataset Statistics */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Database className="h-4 w-4 text-blue-500" />
            Dataset Statistics
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Train</div>
                <div className="text-xl font-bold text-green-600">
                  {dataset.trainCount.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Validation</div>
                <div className="text-xl font-bold text-blue-600">
                  {dataset.valCount.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Total</div>
                <div className="text-xl font-bold">
                  {(dataset.trainCount + dataset.valCount).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Size</div>
                <div className="text-xl font-bold text-purple-600">
                  {dataset.totalSize}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Description */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Tag className="h-4 w-4 text-purple-500" />
            Description
          </h3>
          <p className="text-sm text-muted-foreground">{dataset.description}</p>
        </div>

        <Separator className="my-4" />

        {/* Directory Structure */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FolderTree className="h-4 w-4 text-yellow-500" />
            Directory Structure
          </h3>
          <Card className="bg-muted/30">
            <CardContent className="pt-3 pb-3">
              <pre className="text-xs font-mono text-muted-foreground whitespace-pre overflow-x-auto">
                {getDirectoryStructure(dataset.task)}
              </pre>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-4" />

        {/* Annotation Example */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <FileCode className="h-4 w-4 text-green-500" />
              Annotation Example
            </h3>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2"
              onClick={handleCopyExample}
            >
              {copiedExample ? (
                <>
                  <Check className="h-4 w-4 text-green-500 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Example
                </>
              )}
            </Button>
          </div>
          <Card className="bg-muted/30">
            <CardContent className="pt-3 pb-3">
              <pre className="text-xs font-mono text-muted-foreground whitespace-pre overflow-x-auto max-h-64">
                {getAnnotationExample(dataset.task, dataset.format)}
              </pre>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-4" />

        {/* Labeling Tips */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-yellow-500" />
            Labeling Guidelines
          </h3>
          <div className="space-y-2">
            {labelingTips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Common Mistakes */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-orange-500" />
            Common Mistakes to Avoid
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {commonMistakes.map((mistake, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                <span>{mistake}</span>
              </div>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Dataset Path */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-blue-500" />
            Dataset Path
          </h3>
          <Card className="bg-muted/30">
            <CardContent className="pt-3 pb-3">
              <code className="text-sm font-mono">{dataset.path}</code>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          <Button variant="outline" className="mr-2" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {onSelectDataset && dataset.status === 'ready' && (
            <Button onClick={handleSelect} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Select Dataset
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NeMoDatasetCard;
